import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, RefreshCw, Eye, AlertCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import { stockTransferService } from '../../services/stockTransferService';
import { useAuth } from '../../context/AuthContext';
import { StockTransferApprovalStatusDTO } from '../../types';
import './StockTransferPage.css';

const StockTransferApprovalPage: React.FC = () => {
  const navigate  = useNavigate();
  const { t }     = useTranslation();
  const { user }  = useAuth();

  const [approvals,  setApprovals]  = useState<StockTransferApprovalStatusDTO[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState<string | null>(null);

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await stockTransferService.getPendingApprovals(user.id, 1, 200);
      setApprovals(res.data?.data?.data ?? []);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    if (!user?.id) return;
    setRefreshing(true);
    setError(null);
    try {
      const res = await stockTransferService.getPendingApprovals(user.id, 1, 200);
      setApprovals(res.data?.data?.data ?? []);
    } catch {
      setError(t('stockTransfer.failedToLoad'));
    } finally {
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    { key: 'transferNumber', label: t('stockTransfer.transferNumber'), sortable: true, width: '160px', render: (v) => <code>{v as string}</code> },
    {
      key: 'currentApprovalLevel',
      label: t('stockTransfer.approvalLevel'),
      width: '140px',
      render: (v, row) => {
        const item = row as StockTransferApprovalStatusDTO;
        return `${item.currentApprovalLevel} / ${item.totalApprovalLevels}`;
      },
    },
    {
      key: 'approvalStatus',
      label: t('common.status'),
      width: '150px',
      render: (v) => <span className="ims-badge st-badge-pending">{v as string}</span>,
    },
  ];

  const actions: GridAction[] = [
    {
      icon: <Eye size={16} strokeWidth={1.75} />,
      title: t('stockTransfer.reviewAndApprove'),
      onClick: (row) => navigate(`/stock-transfers/${row.id}`),
      className: 'grid-action-btn-view',
    },
  ];

  return (
    <Layout>
      <div className="st-page">

        {/* ── Header ── */}
        <div className="ims-list-header">
          <div className="ims-list-header-left">
            <h1 className="ims-list-title">
              <ShieldCheck size={22} strokeWidth={1.75} style={{ marginRight: 8 }} />
              {t('stockTransfer.transferApproval')}
            </h1>
            <p className="ims-list-subtitle">{t('stockTransfer.transferApprovalDesc')}</p>
          </div>
          <div className="ims-list-actions">
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={15} strokeWidth={2} className={refreshing ? 'ims-spinning' : ''} />
              {refreshing ? t('common.refreshing', 'Refreshing…') : t('common.refresh')}
            </button>
          </div>
        </div>

        <div className="st-pending-banner">
          <ShieldCheck size={16} strokeWidth={2} />
          {t('stockTransfer.transferApprovalBanner')}
        </div>

        {/* ── Alerts ── */}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <AlertCircle size={16} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'inherit' }}>✕</button>
          </div>
        )}

        {/* ── DataGrid ── */}
        <div className="ims-grid-wrapper">
          <DataGrid
            columns={columns}
            data={approvals}
            loading={loading}
            actions={actions}
            pageSize={10}
            searchPlaceholder={t('common.search', 'Search…')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            emptyMessage={t('stockTransfer.noPendingApprovals')}
          />
        </div>
      </div>
    </Layout>
  );
};

export default StockTransferApprovalPage;
